import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State } from '@stencil/core'

import { Breakpoint, BreakpointObserver, BREAKPOINTS } from '../../../utils/breakpoint-observer'
import Helpers from '../../../utils/helpers'
import { CssClassMap } from '../../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/header?tab=designer).
 *
 * @slot default - Slot para o conteúdo da lista de navegação no cabeçalho.
 */
@Component({
  tag: 'br-header-list',
  styleUrl: 'header-list.scss',
  shadow: true,
})
export class HeaderList {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrHeaderListElement

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * Título da lista.
   */
  @Prop({ reflect: true }) readonly listTitle: string = null

  /**
   * Área atual.
   */
  @State() slotReference: string = null

  /**
   * Indica o breakpoint atual.
   */
  @State() currentBreakpoint: Breakpoint

  /**
   * Indica se o cabeçalho está encolhido.
   */
  @State() isShrunk: boolean = false

  /**
   * Indica se a lista está visível.
   */
  @State() showList: boolean = false

  /**
   * Evento disparado para indicar qual lista deve encolher primeiro.
   * O evento envia o ID do componente pai e o nome da lista.
   * @event headerWidthChange
   */
  @Event({
    eventName: 'headerListUpdate',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  headerListUpdate: EventEmitter<{ id: string; listName: string }>

  /**
   * Evento disparado quando a lista recebe foco.
   * O evento envia o ID do componente.
   * @event headerListFocused
   */
  @Event({
    eventName: 'headerListFocused',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  headerListFocused: EventEmitter<{ id: string }>

  /**
   * Observador de mudanças de breakpoint.
   */
  private breakpointObserver: BreakpointObserver

  /**
   * Referência ao elemento de lista.
   */
  private listElement: HTMLBrListElement

  /**
   * Referência ao botão de disparo.
   */
  private triggerButton: HTMLBrButtonElement

  /**
   * Indica se o botão de disparo foi clicado.
   */
  private triggerClicked: boolean = false

  /**
   * Listener para o evento 'headerWidthChange', disparado pelo componente pai 'br-header'.
   * O evento contém o ID do pai e o nome da lista.
   * Se o ID do pai corresponder ao ID do cabeçalho atual, o evento será tratado.
   * @param event O evento customizado contendo o ID do pai e o nome da lista.
   */
  @Listen('headerWidthChange', { target: 'document', passive: true })
  private handleHeaderWidthChange(event: CustomEvent<{ id: string; listNames: string[]; shrink: boolean }>) {
    if (event.detail.id === this.el.parentElement.shadowRoot.querySelector('.br-header').id) {
      if (event.detail.listNames.includes(this.slotReference)) {
        this.isShrunk = event.detail.shrink
        this.headerListUpdate.emit({ id: event.detail.id, listName: this.slotReference })
      }
    }
  }

  /**
   * Listener para o evento 'headerListFocused', disparado por outras listas.
   * O evento contém o ID da lista que recebeu foco.
   * Se o ID não corresponder ao ID da lista atual, a lista será ocultada.
   * @param event O evento customizado contendo o ID da lista que recebeu foco.
   */
  @Listen('headerListFocused', { target: 'document', passive: true })
  private handleHeaderListFocused(event: CustomEvent<{ id: string }>) {
    if (event.detail.id !== this.customId) {
      this.showList = false
    }
  }

  /**
   * Ciclo de vida do componente.
   * Executado quando o componente é conectado ao DOM.
   */
  connectedCallback() {
    this.setBreakpointObserver()
    this.slotReference = this.el.getAttribute('slot')
  }

  /**
   * Ciclo de vida do componente.
   * Executado uma vez após o componente ser totalmente carregado no DOM.
   */
  componentDidLoad() {
    document.addEventListener('mousedown', this.handleClickOutsideList)

    if (this.triggerButton !== null) {
      this.addTriggerListeners()
    }

    void this.handleHeaderWidthChange
    void this.handleHeaderListFocused
  }

  /**
   * Ciclo de vida do componente.
   * Executado logo após o componente ser atualizado.
   */
  componentDidUpdate() {
    if (this.triggerButton !== null) {
      this.removeTriggerListeners()
      this.addTriggerListeners()
    }
  }

  /**
   * Ciclo de vida do componente.
   * Executado quando o componente é desconectado do DOM.
   */
  disconnectedCallback() {
    document.removeEventListener('mousedown', this.handleClickOutsideList)
    this.removeTriggerListeners()
  }

  /**
   * Fecha a lista.
   */
  @Method()
  async closeList() {
    this.showList = false
  }

  /**
   * Verifica se a lista está aberta.
   * @returns Um booleano indicando se a lista está aberta.
   */
  @Method()
  async isListOpen(): Promise<boolean> {
    return this.showList
  }

  /**
   * Abre a lista.
   */
  @Method()
  async openList(): Promise<void> {
    this.showList = true
  }

  /**
   * Manipula as mudanças de breakpoint.
   * @param breakpoint O breakpoint ativo.
   */
  private handleBreakpointChange = (breakpoint: Breakpoint) => {
    this.currentBreakpoint = breakpoint
    if (BreakpointObserver.getBreakpoints('from', BREAKPOINTS.LARGE).includes(breakpoint)) {
      this.showList = false
    }
  }

  /**
   * Manipula o clique no botão dropdown.
   */
  private handleDropdownButtonClick = () => {
    this.showList = !this.showList
  }

  /**
   * Manipula o clique fora da lista.
   * @param event O evento de Mouse
   */
  private handleClickOutsideList = (event: MouseEvent) => {
    const path = event.composedPath()
    const listClicked = this.listElement && path.includes(this.listElement)

    if (this.showList && !this.triggerClicked && !listClicked) {
      this.showList = false
    }

    this.triggerClicked = false
  }

  /**
   * Manipula o foco no botão de disparo.
   */
  private handleTriggerFocus = () => {
    this.headerListFocused.emit({ id: this.customId })
  }

  /**
   * Manipula o evento mousedown no botão de disparo.
   */
  private handleTriggerMouseDown = () => {
    this.triggerClicked = true
  }

  /**
   * Adiciona os listeners ao botão de disparo.
   */
  private addTriggerListeners() {
    this.triggerButton?.addEventListener('mousedown', this.handleTriggerMouseDown)
    this.triggerButton?.addEventListener('click', this.handleDropdownButtonClick)
    this.triggerButton?.addEventListener('focus', this.handleTriggerFocus)
  }

  /**
   * Define as classes CSS a serem aplicadas ao componente.
   * @returns Um objeto contendo as classes CSS a serem aplicadas ao componente.
   */
  private getCssClassMap(): CssClassMap {
    return {
      'br-header-list': true,
    }
  }

  /**
   * Remove os listeners do botão de disparo.
   */
  private removeTriggerListeners() {
    this.triggerButton?.removeEventListener('mousedown', this.handleTriggerMouseDown)
    this.triggerButton?.removeEventListener('click', this.handleDropdownButtonClick)
    this.triggerButton?.removeEventListener('focus', this.handleTriggerFocus)
  }

  /**
   * Configura o observador de mudanças de breakpoint.
   */
  private setBreakpointObserver() {
    this.breakpointObserver = new BreakpointObserver()
    this.breakpointObserver.subscribe(this.handleBreakpointChange)
  }

  /**
   * Renderiza o componente de lista.
   * @returns JSX do componente de lista.
   */
  private renderList() {
    return (
      <br-list
        ref={(element) => (this.listElement = element)}
        slot={
          BreakpointObserver.getBreakpoints('until', BREAKPOINTS.MEDIUM).includes(this.currentBreakpoint) ||
          this.isShrunk
            ? 'target'
            : null
        }
        is-horizontal={
          BreakpointObserver.getBreakpoints('from', BREAKPOINTS.LARGE).includes(this.currentBreakpoint) &&
          !this.isShrunk
            ? 'true'
            : null
        }
        header={
          BreakpointObserver.getBreakpoints('until', BREAKPOINTS.MEDIUM).includes(this.currentBreakpoint) ||
          this.isShrunk
            ? this.listTitle
            : null
        }
        is-visible={
          BreakpointObserver.getBreakpoints('from', BREAKPOINTS.LARGE).includes(this.currentBreakpoint) &&
          !this.isShrunk
            ? 'true'
            : this.showList
              ? 'true'
              : null
        }
        hide-header-divider
      >
        <slot></slot>
      </br-list>
    )
  }

  /**
   * Renderiza o botão dropdown.
   * @returns JSX do botão dropdown.
   */
  private renderDropdownButton() {
    return (
      <br-button
        ref={(element) => (this.triggerButton = element)}
        slot="trigger"
        shape="circle"
        emphasis="tertiary"
        density="small"
        aria-label={this.listTitle || `Abrir lista de ${this.slotReference}`}
      >
        <br-icon
          icon-name={this.slotReference === 'links' ? 'fa7-solid:ellipsis-vertical' : 'fa7-solid:grid-vertical'}
        ></br-icon>
      </br-button>
    )
  }

  /**
   * Renderiza o componente.
   * @returns JSX do componente.
   */
  render() {
    return (
      <Host>
        <div class={this.getCssClassMap()}>
          {BreakpointObserver.getBreakpoints('until', BREAKPOINTS.MEDIUM).includes(this.currentBreakpoint) ||
          this.isShrunk
            ? this.renderDropdownButton()
            : null}
          {this.renderList()}
        </div>
      </Host>
    )
  }
}
